import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useAuth } from "./useAuth";
import { isObject } from "../utils";
import { toast } from "react-toastify";
import { APP_TYPE, PARENT_COMPANY } from "../config/defaultSettings";
import { setStatusConfigs } from "@/utils/status-configs";
import {
  useGetConfigQuery,
  useFetchConfigMutation,
  useUpdateConfigMutation,
  useDeleteConfigMutation,
} from "../redux/services/config/configApi";
import { AppType } from "@/types/config";

interface IConfigs {
  parentCompany?: {
    appType: AppType;
    [key: string]: any;
  };
  googleMapsApiKey?: string;
  /** Present when API returns `GET /config` with {@link billingEntitlements}. Tier caps + enforcement hints for monetization UX. */
  billingEntitlements?: {
    planTier?: string;
    /** Tenant bypasses tier caps + tier-based feature trimming (Customer.grandfatherFullAccess). */
    grandfatherFullAccess?: boolean;
    monitorOnly?: boolean;
    seatCapEnforcementMode?: "hard" | "monitor";
    tierLimits?: {
      officeSeats?: number | null;
      driverSeats?: number | null;
      savedReports?: number | null;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

export interface IConfigContext {
  configs: IConfigs;
  addConfig: (newConfigItem: any) => Promise<void>;
  deleteConfig: (configData: any) => Promise<void>;
  appType: AppType | string | null;
  parentCompany: string | null;
  loading: boolean;
  cleanUpConfig: () => void;
}

const contextDefaultValues: IConfigContext = {
  configs: {},
  addConfig: () => Promise.resolve(),
  deleteConfig: () => Promise.resolve(),
  appType: "",
  parentCompany: "",
  loading: true,
  cleanUpConfig: () => {},
};

export const ConfigContext =
  createContext<IConfigContext>(contextDefaultValues);

export const ConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [configs, setConfigs] = useState<IConfigs>({});
  const { user, initialized, isAuthenticated } = useAuth();
  const [configInitialized, setConfigInitialized] = useState(false);
  const parentCompany = localStorage.getItem(PARENT_COMPANY);
  const appType = localStorage.getItem(APP_TYPE);

  const [updateConfig, { isLoading: updateConfigLoading }] =
    useUpdateConfigMutation();
  const [deleteConfigItem, { isLoading: deleteConfigLoading }] =
    useDeleteConfigMutation();

  // Use the query instead of mutation for fetching config
  const {
    data: configData,
    isLoading: getConfigLoading,
    isError: getConfigError,
  } = useGetConfigQuery(
    { parentCompany },
    {
      skip: !parentCompany || !user?.active || !initialized || !isAuthenticated,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );

  useEffect(() => {
    if (configData && !configInitialized) {
      const { config } = configData;
      localStorage.setItem(APP_TYPE, config.configs.parentCompany.appType);
      setConfigs(config ? config.configs : {});
      setStatusConfigs(config?.configs?.statusConfigs || null);
      setConfigInitialized(true);

      // Opt-in debug logging (browser console):
      //   localStorage.setItem("debugRates", "true"); location.reload();
      try {
        const shouldDebugRates =
          typeof window !== "undefined" &&
          window?.localStorage?.getItem("debugRates") === "true";
        if (shouldDebugRates) {
          // eslint-disable-next-line no-console
          console.groupCollapsed("[Config][debugRates] Config loaded");
          // eslint-disable-next-line no-console
          console.log("parentCompany:", parentCompany);
          // eslint-disable-next-line no-console
          console.log("top-level config keys:", Object.keys(config?.configs || {}));
          // eslint-disable-next-line no-console
          console.log("configs.rates:", config?.configs?.rates);
          // eslint-disable-next-line no-console
          console.groupEnd();
        }
      } catch {
        // no-op
      }
    }
  }, [configData, configInitialized]);

  // Fallback: if conditions are not met for config loading, still initialize
  useEffect(() => {
    if (
      !configInitialized &&
      initialized &&
      (!parentCompany || !user?.active || !isAuthenticated)
    ) {
      // If we can't load config due to missing conditions, still initialize to prevent infinite loading
      setConfigInitialized(true);
    }
  }, [
    configInitialized,
    initialized,
    parentCompany,
    user?.active,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (getConfigError) {
      console.error("[Config] Failed to fetch configurations:", getConfigError);
      toast.error("Failed to fetch configurations. Please try again later.");
    }
  }, [getConfigError]);

  useEffect(() => {
    if (!initialized || !isAuthenticated) {
      setConfigs({});
      setConfigInitialized(false);
      localStorage.removeItem(APP_TYPE);
      setStatusConfigs(null);
    }
  }, [initialized, isAuthenticated]);

  const addConfig = useCallback(
    async (newConfigItem) => {
      if (!parentCompany) {
        return;
      }

      try {
        const response = await updateConfig({
          parentCompany,
          data: newConfigItem,
        }).unwrap();

        const data = response;

        if (data.status === "success") {
          setConfigs(data.config.configs);
          toast.success(data.message);
        }
      } catch (error) {
        console.error("[Config] Failed to add config:", error);
        toast.error("Failed to add configuration");
      }
    },
    [parentCompany, updateConfig]
  );

  const deleteConfig = useCallback(
    async (configData) => {
      if (!parentCompany) {
        console.warn("[Config] Delete config aborted - no parent company");
        return;
      }

      try {
        const response = await deleteConfigItem({
          parentCompany,
          data: configData,
        }).unwrap();
        // const response = await fetch(`/config/${parentCompany}`, {
        //   method: "DELETE",
        //   headers: {
        //   "Content-Type": "application/json",
        // },
        //   body: JSON.stringify(configData),
        //   credentials: "include",
        // });

        const data = response;

        if (data.status === "success") {
          setConfigs(data.config.configs);
          toast.success(data.message);
        }
      } catch (error) {
        console.error("[Config] Failed to delete config:", error);
        toast.error("Failed to delete configuration");
      }
    },
    [parentCompany, deleteConfigItem]
  );

  const cleanUpConfig = useCallback(() => {
    setConfigs({});
    setConfigInitialized(false);
    localStorage.removeItem(APP_TYPE);
  }, []);

  const contextValue = useMemo(() => {
    return {
      configs: configs || {},
      addConfig,
      deleteConfig,
      parentCompany,
      appType: configs.parentCompany?.appType || appType,
      loading:
        !configInitialized ||
        getConfigLoading ||
        updateConfigLoading ||
        deleteConfigLoading,
      cleanUpConfig,
    };
  }, [
    configs,
    addConfig,
    deleteConfig,
    parentCompany,
    appType,
    configInitialized,
    getConfigLoading,
    updateConfigLoading,
    deleteConfigLoading,
    cleanUpConfig,
  ]);

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};

export default ConfigProvider;
